/**
 * Wrapper around generated RTK Query API with cleaner naming and custom logic.
 *
 * All endpoint references (keys, hooks, endpoint map) are derived from the
 * central definitions in `./config.ts` so generated names live in one place.
 */
import { generatedApi } from './api.generated';
import {
  createEndpointExports,
  createHookExports,
  provideListTags,
  LIST_TAG_ID,
} from '@skynet/data-helpers';
import type { HookExports } from '@skynet/data-helpers';
import { MARKETING_CAMPAIGNS_API_ENDPOINTS as cfg } from './config';

/**
 * Cache tags follow the recommended `id: 'LIST'` recipe:
 * https://redux-toolkit.js.org/rtk-query/usage/automated-refetching#recipes
 *
 * A single `MarketingCampaigns` tag type is used for both the collection and
 * individual entities. The list query provides a per-item tag for every
 * campaign plus a dedicated `{ type: 'MarketingCampaigns', id: 'LIST' }` tag,
 * so that:
 * - updating/deleting a single campaign invalidates only that campaign's
 *   `{ id }` tag, which refetches both the affected `getMarketingCampaign`
 *   query and the list (the list provides that same per-item tag);
 * - creating a campaign invalidates `{ id: 'LIST' }` to refetch the collection
 *   without touching unrelated entities.
 *
 * This replaces the previous manual `dispatch(invalidateTags(...))` /
 * `updateQueryData` workaround in `onQueryStarted`: that was attributed to
 * redux-toolkit#5093, but that issue is about the `isSuccess` flag during
 * pessimistic updates, not tag invalidation — `invalidatesTags` propagates to
 * the list correctly via the per-item tags.
 *
 * `MarketingSources` and `MarketingMediums` are static reference lists with no
 * mutations; each gets a single `{ id: 'LIST' }` collection tag for consistency.
 */
export const marketingCampaignsApi = generatedApi.enhanceEndpoints({
  addTagTypes: ['MarketingCampaigns', 'MarketingSources', 'MarketingMediums'],
  endpoints: {
    [cfg.createMarketingCampaign.generated]: {
      invalidatesTags: [{ type: 'MarketingCampaigns', id: LIST_TAG_ID }],
    },
    [cfg.listMarketingCampaigns.generated]: {
      providesTags: (result) => provideListTags(result, 'MarketingCampaigns'),
    },
    // Reference list — static lookup data, no mutations invalidate it.
    [cfg.listMarketingSources.generated]: {
      providesTags: [{ type: 'MarketingSources', id: LIST_TAG_ID }],
    },
    // Reference list — static lookup data, no mutations invalidate it.
    [cfg.listMarketingMediums.generated]: {
      providesTags: [{ type: 'MarketingMediums', id: LIST_TAG_ID }],
    },
    [cfg.getMarketingCampaign.generated]: {
      providesTags: (_result, _error, arg) => [{ type: 'MarketingCampaigns', id: arg.id }],
    },
    [cfg.updateMarketingCampaign.generated]: {
      invalidatesTags: (_result, _error, arg) => [{ type: 'MarketingCampaigns', id: arg.id }],
    },
    [cfg.deleteMarketingCampaign.generated]: {
      invalidatesTags: (_result, _error, arg) => [{ type: 'MarketingCampaigns', id: arg.id }],
    },
  },
});

export const hookExports: HookExports<typeof cfg, typeof marketingCampaignsApi> = createHookExports(
  cfg,
  marketingCampaignsApi,
);

const endpointExports = createEndpointExports(cfg, marketingCampaignsApi);

export const marketingCampaignsApiEndpoints: typeof endpointExports = endpointExports;
